/** Manage → Import: bring a Mantis database (and optionally its attachment files) into the team. */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { useMeta } from '../meta';
import { ErrorBox, Panel } from '../components';
import { t, tError, tp } from '../i18n';

interface Summary {
  counts: { projects: number; issues: number; notes: number; users: number; attachments: number };
  projects: { name: string; issues: number }[];
  users: { id: number; username: string; realname: string; enabled: boolean; access_level: number; suggested_user_id: number | null }[];
  files_needed: number;
  numbers_in_this_team: number;
  numbers_in_other_teams: number;
}
interface Job {
  id: string;
  status: 'uploaded' | 'staging' | 'ready' | 'importing' | 'done' | 'failed' | 'cancelled';
  message: string;
  dump_name: string;
  summary: Summary | null;
  options: { user_map?: Record<string, number | null>; replace_issues?: boolean } | null;
  report: {
    counts: Record<string, number>;
    newUsers: string[];
    matchedUsers: string[];
    newProjects: string[];
    matchedProjects: string[];
    warnings: string[];
    renumberedIssues?: [number, number][];
  } | null;
  error: string | null;
  files_received: number;
}

/** POST with upload progress (fetch can't report it). */
function upload<T>(url: string, data: FormData, onProgress: (share: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let body: any = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        /* not JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as T);
      else reject(new ApiError(xhr.status, body?.error ?? xhr.statusText));
    };
    xhr.onerror = () => reject(new Error(t('The upload was interrupted.')));
    xhr.send(data);
  });
}

export function ImportTab() {
  const qc = useQueryClient();
  const { data: job, error } = useQuery({
    queryKey: ['import-job'],
    queryFn: () => api.get<Job | null>('/api/manage/import/latest'),
    refetchInterval: (q) => (q.state.data && ['staging', 'importing'].includes(q.state.data.status) ? 2000 : false),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['import-job'] });
  const cancel = useMutation({ mutationFn: (id: string) => api.del(`/api/manage/import/${id}`), onSuccess: refresh });

  const active = job && !['done', 'failed', 'cancelled'].includes(job.status) ? job : null;
  return (
    <>
      <ErrorBox error={error || cancel.error} />
      {!active && <UploadStep last={job ?? null} onStarted={refresh} />}
      {active?.status === 'staging' && (
        <Panel title={t('Reading the dump')}>
          <p>
            <span className="spinner" /> {active.message || t('Starting…')}
          </p>
          <p className="muted small">{t('Big databases take a while (about 25 minutes for 20,000 issues). You can leave this page and come back later.')}</p>
        </Panel>
      )}
      {active?.status === 'ready' && active.summary && <ReadyStep job={active} onChange={refresh} onCancel={() => cancel.mutate(active.id)} />}
      {active?.status === 'importing' && (
        <Panel title={t('Importing')}>
          <p>
            <span className="spinner" /> {active.message || t('Starting…')}
          </p>
          <p className="muted small">{t('Everything is written in one step at the end; until then the team does not change.')}</p>
        </Panel>
      )}
    </>
  );
}

function UploadStep({ last, onStarted }: { last: Job | null; onStarted: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [share, setShare] = useState<number | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const start = async () => {
    if (!file) return;
    setErr(null);
    setShare(0);
    try {
      const fd = new FormData();
      fd.append('dump', file, file.name);
      await upload('/api/manage/import', fd, setShare);
      onStarted();
    } catch (e) {
      setErr(e);
    } finally {
      setShare(null);
    }
  };
  return (
    <>
      {last?.status === 'done' && last.report && <ImportReport job={last} />}
      {last?.status === 'failed' && <div className="banner error">{t('The last import failed: {error}', { error: tError(last.error ?? '') })}</div>}
      <Panel title={t('Import from Mantis')}>
        <p>{t('Brings projects, issues with their numbers, notes, history, tags, relationships and people from a MantisBT database into this team. People can be matched with the members of the team; everyone else gets an account that keeps their Mantis password.')}</p>
        <div className="form-grid">
          <label>
            {t('Mantis database dump (.sql or .sql.gz)')}
            <input type="file" accept=".sql,.gz,application/sql,application/gzip" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label>
        </div>
        <p className="muted small">{t('Make the dump with mysqldump or the phpMyAdmin export (SQL format). The attachment folder can be chosen in the next step.')}</p>
        <ErrorBox error={err ? new Error(tError((err as Error).message)) : null} />
        {share != null && <Progress share={share} label={t('Uploading the dump')} />}
        <div className="buttons">
          <button className="primary" disabled={!file || share != null} onClick={start}>
            {t('Upload and read')}
          </button>
        </div>
      </Panel>
    </>
  );
}

function Progress({ share, label }: { share: number; label: string }) {
  return (
    <div className="progress" title={label}>
      <div style={{ width: `${Math.round(share * 100)}%` }} />
      <span>
        {label} — {Math.round(share * 100)}%
      </span>
    </div>
  );
}

function ReadyStep({ job, onChange, onCancel }: { job: Job; onChange: () => void; onCancel: () => void }) {
  const { meta, label } = useMeta();
  const s = job.summary!;
  const members = useQuery({ queryKey: ['users'], queryFn: () => api.get<{ id: number; username: string; realname: string; enabled: boolean }[]>('/api/users') });
  // a failed run keeps the choices made for it
  const [map, setMap] = useState<Record<string, number | null>>(() => ({
    ...Object.fromEntries(s.users.map((u) => [u.username, u.suggested_user_id])),
    ...job.options?.user_map,
  }));
  const [replace, setReplace] = useState(!!job.options?.replace_issues);
  const run = useMutation({
    mutationFn: () => api.post(`/api/manage/import/${job.id}/run`, { user_map: map, replace_issues: replace, renumber_issues: true }),
    onSuccess: onChange,
  });
  const taken = s.numbers_in_this_team + s.numbers_in_other_teams;

  return (
    <>
      {job.error && <div className="banner error">{t('The import did not finish: {error}', { error: tError(job.error) })}</div>}
      <Panel title={t('What the dump contains')}>
        <p>
          <strong>{job.dump_name}</strong> · {tp(s.counts.projects, '{n} project', '{n} projects')} · {tp(s.counts.issues, '{n} issue', '{n} issues')} ·{' '}
          {tp(s.counts.notes, '{n} note', '{n} notes')} · {tp(s.counts.users, '{n} person', '{n} people')} · {tp(s.counts.attachments, '{n} attachment', '{n} attachments')}
        </p>
        <details>
          <summary>{t('Projects')}</summary>
          <ul className="columns">
            {s.projects.map((p) => (
              <li key={p.name}>
                {p.name} <span className="subtle small">({p.issues})</span>
              </li>
            ))}
          </ul>
        </details>
        {taken > 0 && (
          <div className="banner warning">
            <p>{tp(taken, '{n} issue number of this dump is already used on this server.', '{n} issue numbers of this dump are already used on this server.')}</p>
            {s.numbers_in_this_team > 0 && (
              <label className="check">
                <input type="radio" name="numbers" checked={replace} onChange={() => setReplace(true)} />{' '}
                {tp(s.numbers_in_this_team, 'Replace {n} issue of this team that has the same number (its notes and history are replaced too)', 'Replace {n} issues of this team that have the same numbers (their notes and history are replaced too)')}
              </label>
            )}
            <label className="check">
              {s.numbers_in_this_team > 0 && <input type="radio" name="numbers" checked={!replace} onChange={() => setReplace(false)} />}{' '}
              {t('Give these issues new numbers; references like #123 in texts are updated. The list of new numbers can be downloaded after the import.')}
            </label>
            {replace && s.numbers_in_other_teams > 0 && (
              <p className="small">{tp(s.numbers_in_other_teams, 'The {n} issue whose number another team uses gets a new number.', 'The {n} issues whose numbers other teams use get new numbers.')}</p>
            )}
          </div>
        )}
      </Panel>

      <Panel title={t('People')}>
        <p className="muted small">{t('Choose who each Mantis user is in this team. "New account" creates an account with the Mantis username and password and adds it to the team.')}</p>
        <table className="simple">
          <thead>
            <tr>
              <th>{t('Mantis user')}</th>
              <th>{t('Access Level')}</th>
              <th>{t('In this team')}</th>
            </tr>
          </thead>
          <tbody>
            {s.users.map((u) => (
              <tr key={u.id} className={u.enabled ? '' : 'disabled'}>
                <td>
                  {u.username} <span className="subtle">{u.realname}</span> {!u.enabled && <span className="pill">{t('disabled')}</span>}
                </td>
                <td>{label('access_level', u.access_level)}</td>
                <td>
                  <select value={map[u.username] ?? ''} onChange={(e) => setMap({ ...map, [u.username]: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">{t('New account')}</option>
                    {(members.data ?? []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.username}
                        {m.realname ? ` (${m.realname})` : ''}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <AttachmentsStep job={job} onChange={onChange} />

      <Panel title={t('Start')}>
        <ErrorBox error={run.error ? new Error(tError((run.error as Error).message)) : null} />
        <div className="buttons">
          <button className="primary" disabled={run.isPending} onClick={() => run.mutate()}>
            {t('Import into this team')}
          </button>
          <button onClick={() => confirm(t('Cancel this import? The uploaded dump and files are deleted.')) && onCancel()}>{t('Cancel import')}</button>
        </div>
      </Panel>
    </>
  );
}

function AttachmentsStep({ job, onChange }: { job: Job; onChange: () => void }) {
  const s = job.summary!;
  const [picked, setPicked] = useState<File[] | null>(null);
  const [share, setShare] = useState<number | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const needed = useQuery({
    queryKey: ['import-needed', job.id, job.files_received],
    queryFn: () => api.get<string[]>(`/api/manage/import/${job.id}/needed-files`),
    enabled: s.files_needed > 0,
  });
  const neededSet = useMemo(() => new Set(needed.data ?? []), [needed.data]);
  const matching = useMemo(() => (picked ?? []).filter((f) => neededSet.has(f.name)), [picked, neededSet]);
  const [input, setInput] = useState<HTMLInputElement | null>(null);
  useEffect(() => {
    input?.setAttribute('webkitdirectory', '');
  }, [input]);

  const send = async () => {
    setErr(null);
    setShare(0);
    try {
      // batches of up to 100 files / 50 MB
      let batch: File[] = [];
      let size = 0;
      let done = 0;
      const flush = async () => {
        if (!batch.length) return;
        const fd = new FormData();
        for (const f of batch) fd.append('files', f, f.name);
        await upload(`/api/manage/import/${job.id}/files`, fd, () => {});
        done += batch.length;
        setShare(done / matching.length);
        batch = [];
        size = 0;
      };
      for (const f of matching) {
        batch.push(f);
        size += f.size;
        if (batch.length >= 100 || size >= 50 * 1024 * 1024) await flush();
      }
      await flush();
      setPicked(null);
      onChange();
    } catch (e) {
      setErr(e);
    } finally {
      setShare(null);
    }
  };

  if (s.files_needed === 0) return null;
  return (
    <Panel title={t('Attachments (optional)')}>
      <p className="muted small">
        {t('Mantis kept {n} attachment files on its server disk. Choose that upload folder to bring the files along; without it, the attachments are listed but their files are missing (they can be added with a new import later).', { n: s.files_needed })}
      </p>
      <p>
        {t('Files received: {received} of {needed}', { received: job.files_received, needed: s.files_needed })}
      </p>
      <div className="form-grid">
        <label>
          {t('Mantis upload folder')}
          <input ref={setInput} type="file" multiple onChange={(e) => setPicked([...(e.target.files ?? [])])} />
        </label>
      </div>
      {picked && <p>{t('{found} of the {all} files in the folder belong to this dump.', { found: matching.length, all: picked.length })}</p>}
      <ErrorBox error={err ? new Error(tError((err as Error).message)) : null} />
      {share != null && <Progress share={share} label={t('Uploading files')} />}
      <div className="buttons">
        <button disabled={!matching.length || share != null} onClick={send}>
          {tp(matching.length, 'Upload {n} file', 'Upload {n} files')}
        </button>
      </div>
    </Panel>
  );
}

function ImportReport({ job }: { job: Job }) {
  const r = job.report!;
  const rows: [string, number | undefined][] = [
    [t('Issues'), r.counts.issues],
    [t('Notes'), r.counts.notes],
    [t('History entries'), r.counts.history],
    [t('New projects'), r.newProjects.length],
    [t('Existing projects'), r.matchedProjects.length],
    [t('New accounts'), r.newUsers.length],
    [t('Matched people'), r.matchedUsers.length],
    [t('Attachments'), r.counts.attachments],
    [t('Attachment files'), r.counts['attachment files']],
    [t('Replaced issues'), r.counts['issues replaced']],
    [t('Issues with new numbers'), r.counts['issues renumbered']],
  ];
  const renumbered = r.renumberedIssues;
  const csv = useMemo(() => {
    if (!renumbered?.length) return null;
    const lines = ['old,new', ...renumbered.map(([a, b]) => `${a},${b}`)];
    return URL.createObjectURL(new Blob([lines.join('\n') + '\n'], { type: 'text/csv' }));
  }, [renumbered]);
  return (
    <Panel title={t('Import finished')}>
      <div className="banner success">
        {t('"{name}" was imported into the team.', { name: job.dump_name })} <Link to="/issues">{t('Go to the issues')}</Link>
      </div>
      <table className="simple">
        <tbody>
          {rows
            .filter(([, v]) => v != null)
            .map(([k, v]) => (
              <tr key={k}>
                <td>{k}</td>
                <td className="num">{v}</td>
              </tr>
            ))}
        </tbody>
      </table>
      {r.newUsers.length > 0 && (
        <p className="small">
          {t('New accounts (they log in with their Mantis password):')} {r.newUsers.join(', ')}
        </p>
      )}
      {csv && (
        <p className="small">
          <a href={csv} download={`new-issue-numbers-${job.id}.csv`}>
            {t('Download the list of new issue numbers (CSV)')}
          </a>
        </p>
      )}
      {r.warnings.length > 0 && (
        <details>
          <summary>{tp(r.warnings.length, '{n} warning', '{n} warnings')}</summary>
          <ul className="small">
            {r.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </details>
      )}
    </Panel>
  );
}
